import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled, { ThemeProvider, DefaultTheme, keyframes, css, createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import  tracksData  from './data/tracks';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  User,
  Search,
  DollarSign
} from 'lucide-react';



// --- Types ---
interface Track {
  title: string;
  artist: string;
  src: string;
  cover: string;
  id?: string; // Optional ID field
}

interface Character {
    static: string;
    animated: string;
}

// Using character data from the user's initial code snippet
// NOTE: Ensure these image paths are correct relative to your public folder or server setup.
const characterData: Character[] = [
  { static: '/anime/gojo.png', animated: '/anime/gojo.gif' },
  { static: '/anime/purple.png', animated: '/anime/purple.gif' },
  { static: '/anime/chika.png', animated: '/anime/chika.gif' },
  { static: '/anime/Paw.png', animated: '/anime/Paw.gif' },
];

// --- Themes ---
// Define a base theme structure
const baseTheme = {
  borderRadius: { small: '4px', medium: '16px', large: '16px', pill: '9999px' },
  shadows: { small: '0 2px 4px rgba(0, 0, 0, 0.1)', medium: '0 4px 8px rgba(0, 0, 0, 0.15)', large: '0 8px 16px rgba(0, 0, 0, 0.2)' },
  fonts: { body: 'Inter, sans-serif', branding: "'Arial Black', Gadget, sans-serif" },
};

// Define the light theme extending the base
const lightTheme: DefaultTheme = {
  ...baseTheme,
  colors: {
    primary: '#C80003', secondary: '#FFFFFF', background: '#f3e8e8', textOnPrimary: '#FFFFFF',
    textLight: '#1f2937', textDark: '#1f2937', textMuted: '#6b7280', border: '#e5e7eb',
    primaryDarker: '#8B1802', primaryLighter: '#fee2e2',
  },
};

// Define the dark theme extending the base
const darkTheme: DefaultTheme = {
  ...baseTheme,
  colors: {
    primary: '#C80003', secondary: '#1f2937', background: '#374151', textOnPrimary: '#FFFFFF',
    textLight: '#f9fafb', textDark: '#f9fafb', textMuted: '#9ca3af', border: '#4b5563',
    primaryDarker: '#8B1802', primaryLighter: '#7f1d1d',
  },
};

// --- Global Styles ---
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: ${({ theme }) => theme.fonts.body}; background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.textDark}; line-height: 1.5;
    -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;
  }
  button, input[type="button"], input[type="submit"] { cursor: pointer; font-family: inherit; }
  input, textarea, select { font-family: inherit; }
  img { max-width: 100%; display: block; onerror: this.onerror=null; this.src='https://placehold.co/100x100/cccccc/ffffff?text=Error'; }
  ::-webkit-scrollbar { width: 6px; height: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.textMuted}; border-radius: ${({ theme }) => theme.borderRadius.pill}; }
  ::-webkit-scrollbar-thumb:hover { background: ${({ theme }) => theme.colors.primary}; }
`;

// Extend DefaultTheme for TypeScript support with styled-components
declare module 'styled-components' {
  export interface DefaultTheme {
    borderRadius: { small: string; medium: string; large: string; pill: string; };
    shadows: { small: string; medium: string; large: string; };
    fonts: { body: string; branding: string; };
    colors: {
      primary: string; secondary: string; background: string; textOnPrimary: string;
      textLight: string; textDark: string; textMuted: string; border: string;
      primaryDarker: string; primaryLighter: string;
    };
  }
}

// --- Animation Keyframes ---
const marquee = keyframes` 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } `;

// --- Styled Components ---

const AppContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.background};
  transition: background-color 0.3s ease;
`;

// Main container using SVG clip-paths
const PlayerContainer = styled.div`
  display: flex; // Default to flex for desktop
  width: 100%;
  height: 745px; // Default height for desktop
  position: relative;
  filter: drop-shadow(0 10px 15px rgba(0, 0, 0, 0.1)) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));

  // Desktop styles (overlap) - Use the width calculation from user's code
  max-width: calc(496px + 400px - 125px); // Adjusted based on RightPanel width and desired overlap

  // Mobile styles (stacking)
  @media (max-width: 900px) {
    flex-direction: column;
    max-width: 450px; // Mobile max width
    height: auto; // Auto height for mobile
  }
`;

// Base Panel Style (Common properties for both panels)
const PanelBase = styled(motion.div)`
  // Default styles (Desktop - Absolute Positioning)
  position: absolute;
  top: 0;
  height: 100%;
  padding: 30px 25px;
  display: flex;
  flex-direction: column;
  transition: background-color 0.3s ease, color 0.3s ease;

  // Mobile styles (Relative Positioning, Stacking)
  @media (max-width: 900px) {
    position: relative; // Override absolute
    width: 100% !important; // Ensure full width
    left: 0 !important; // Reset horizontal positioning
    right: 0 !important;
    height: auto; // Auto height for content
    clip-path: none !important; // Remove clip path
    border-radius: 20px; // Standard radius
    margin-bottom: 15px; // Space between stacked panels
    margin-right: 0 !important; // Reset desktop margin
    z-index: auto !important; // Reset z-index
    &:last-child {
        margin-bottom: 0;
    }
  }
`;

// Left Panel
const LeftPanel = styled(PanelBase)`
  // Desktop styles
  left: 0;
  width: 500px;
  clip-path: url(#leftPanelClip);
  z-index: 1; // On top for overlap

  // Shared styles
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textOnPrimary};
  justify-content: space-between;


  // Mobile specific overrides (if any beyond PanelBase)
  @media (max-width: 900px) {
     border-bottom-left-radius: 0; // Adjust radius for stacking
     border-bottom-right-radius: 0;
  }
`;

// Right Panel
const RightPanel = styled(PanelBase)`
  // Default styles (Mobile - these are overridden by PanelBase mobile styles anyway)
  width: 100%;
  margin-right: 0;

  // Desktop styles (Apply user's specific layout)
  @media (min-width: 901px) {
      right: 0;
      width: 400px; // User specified width
      margin-right: -50px; // User specified negative margin for overlap
      clip-path: url(#rightPanelClip);
      z-index: 0; // Behind left panel
  }

  // Shared styles
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.textDark};
  justify-content: space-between;

  // Mobile specific overrides (if any beyond PanelBase)
  @media (max-width: 900px) {
     border-top-left-radius: 0; // Adjust radius for stacking
     border-top-right-radius: 0;
     // Other resets handled by PanelBase mobile styles
  }
`;


// --- Left Panel Components ---

const LogoPlaceholder = styled.div`
  width: 40px; height: 40px; border-radius: 50%; display: flex; justify-content: center;
  align-items: center; font-weight: bold; color: ${({ theme }) => theme.colors.primary};
  background-color: ${({ theme }) => theme.colors.textOnPrimary}; position: absolute;
  top: 35px; left: 35px;
  img { width: 60%; height: 60%; object-fit: contain; }
`;

const CharacterDisplay = styled.div`
  flex-grow: 1; display: flex; justify-content: center; align-items: center;
  margin-top: 70px; margin-bottom: 20px; position: relative; min-height: 350px; overflow: hidden;
`;

const CharacterImage = styled(motion.img)`
  // Default styles (Mobile)
  max-height: 100%; // Slightly smaller on mobile
  max-width: 100%;
  margin-left: -90px;

  // Shared styles
  object-fit: contain;
  height: auto;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);

  // Desktop specific offset
  @media (min-width: 901px) {
      max-height: 100%; // Allow full height on desktop
      max-width: 100%;
      margin-left: -150px; // Apply user's offset only on desktop
  }
`;

const VolumeContainer = styled.div`
  position: absolute; left: 35px; top: 50%; transform: translateY(-50%); height: 50%;
  width: 10px; background-color: rgba(255, 255, 255, 0.2); border-radius: ${({ theme }) => theme.borderRadius.pill};
  display: flex; align-items: flex-end; cursor: pointer; overflow: hidden;
`;
const VolumeLevel = styled.div<{ $level: number }>`
  width: 100%; height: ${({ $level }) => $level}%; background-color: ${({ theme }) => theme.colors.textOnPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.pill}; transition: height 0.1s linear; pointer-events: none;
`;
const VolumeInput = styled.input`
  position: absolute; left: 50%; top: 0; transform: translateX(-50%); height: 100%; width: 30px;
  cursor: pointer; opacity: 0; z-index: 1; margin: 0; writing-mode: bt-lr; -webkit-appearance: slider-vertical;
`;

const SongTitleContainer = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.primaryLighter + '40'}; border: 1px solid rgba(255, 255, 255, 0.4);
  padding: 10px 0; border-radius: ${({ theme }) => theme.borderRadius.medium}; text-align: center;
  margin: 0 auto 15px auto; max-width: 60%; overflow: hidden; position: relative;
`;
const SongTitleTextWrapper = styled.div<{ $isPlaying: boolean; $textWidth?: number }>`
  display: inline-block; white-space: nowrap; padding: 0 15px; color: ${({ theme }) => theme.colors.textOnPrimary};
  font-size: 1.2em; font-weight: 600;
  ${({ $isPlaying, $textWidth = 0, theme }) => {
    const containerWidthEstimate = 496 * 0.7 * 0.9; // Estimate based on LeftPanel width
    return $isPlaying && $textWidth > containerWidthEstimate
      ? css`animation: ${marquee} ${Math.max(5, $textWidth / 30)}s linear infinite;`
      : css`animation: none; max-width: 100%; overflow: hidden; text-overflow: ellipsis;`;
  }}
`;
const SongTitle = styled.h2` margin: 0; display: inline-block; `;

const CharacterSelector = styled.div` display: flex; justify-content: center; gap: 15px; margin-bottom: 10px; `;
const CharacterDot = styled.button<{ $isActive: boolean }>`
  width: 30px; height: 30px; border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme }) => theme.colors.primaryLighter + '40'}; border: 1px solid rgba(255, 255, 255, 0.4);
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.6)}; cursor: pointer; transition: all 0.2s ease;
  display: flex; justify-content: center; align-items: center; color: white;
  svg { width: 16px; height: 16px; }
  &:hover { opacity: 1; border-color: white; transform: scale(1.1); }
`;

// --- Right Panel Components ---

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
  width: 100%; // Default width (Mobile)
  margin-left: 0;
  margin-right: 0;

  // Desktop specific margins/width
  @media (min-width: 901px) {
      margin-right: -100px; // User specified
      margin-left: 95px; // User specified
      width: 80%; // User specified
  }
`;

const SearchInput = styled.input`
  width: 100%; // Default width (Mobile)
  padding: 10px 15px; border-radius: ${({ theme }) => theme.borderRadius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border}; background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.textDark}; font-size: 0.9em; text-align: center;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; }
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary + '40'}; }

  // Desktop specific width adjustment
   @media (min-width: 901px) {
       width: 90%; // User's desktop width setting
   }
`;

const TracklistContainer = styled.div`
  overflow-y: auto; margin-bottom: 20px; padding-right: 5px; flex-grow: 1; min-height: 150px;
  width: 100%; // Default width (Mobile)
  margin-left: 0;
  margin-right: 0;

  // Desktop specific margins/width
  @media (min-width: 901px) {
      margin-right: -100px; // User specified
      margin-left: 95px; // User specified
      width: 75%; // User specified
  }

  // Mobile height constraint
  @media (max-width: 900px) {
     max-height: 250px;
     flex-grow: 0; // Prevent excessive growth on mobile
  }
`;

const TracklistItem = styled(motion.button)<{ $isActive: boolean }>`
  display: block; width: 100%; background-color: ${({ theme, $isActive }) => ($isActive ? theme.colors.primary : theme.colors.primaryDarker)};
  border: none; text-align: center; padding: 10px 15px; margin-bottom: 10px; border-radius: ${({ theme }) => theme.borderRadius.pill};
  cursor: pointer; font-size: 0.95em; color: ${({ theme }) => theme.colors.textOnPrimary}; font-weight: 500;
  transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
  box-shadow: ${({ theme, $isActive }) => ($isActive ? `0 4px 10px ${theme.colors.primary + '80'}` : theme.shadows.small)};
  opacity: ${({ $isActive }) => ($isActive ? 1 : 0.9)};
  &:hover { opacity: 1; filter: ${({ $isActive }) => ($isActive ? 'brightness(1.1)' : 'brightness(1.05)')}; box-shadow: ${({ theme }) => theme.shadows.medium}; }
  &:active { transform: scale(0.98); }
`;

const PlayerControlsSection = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 15px; padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border}; margin-top: auto;
`;

const CoverArtAndThemeToggle = styled.div`
    display: flex; align-items: center; justify-content: center;
    gap: 20px; // Default gap (Mobile)
    width: 100%;
    margin-left: 0; // Default margin (Mobile)

    // Desktop specific styles
    @media (min-width: 901px) {
        margin-left: 50px; // Apply user's offset only on desktop
        gap: 15px; // Apply user's gap only on desktop
    }
`;

const ThemeToggleSlider = styled.div`
  width: 24px; height: 50px; border-radius: ${({ theme }) => theme.borderRadius.pill};
  background-color: ${({ theme }) => theme.colors.textMuted + '40'}; position: relative; cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.textMuted + '80'}; transition: background-color 0.3s ease, border-color 0.3s ease;
  flex-shrink: 0;
  &::after { content: ''; position: absolute; width: 18px; height: 18px; border-radius: 50%; background-color: ${({ theme }) => theme.colors.primary};
    left: 50%; transform: translateX(-50%); top: ${({ theme }) => (theme.colors.secondary === '#FFFFFF' ? '3px' : 'calc(100% - 18px - 3px)')};
    transition: top 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55); }
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const CoverArtContainer = styled(motion.div)`
  position: relative;
  width: 100px; // Default/Mobile size
  height: 100px;
  border-radius: ${({ theme }) => theme.borderRadius.medium}; overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.medium}; flex-shrink: 0;

  // Desktop specific size
   @media (min-width: 901px) {
       width: 170px; // User's desktop width
   }
`;
const CoverArtImage = styled.img` width: 100%; height: 100%; object-fit: cover; `;

const PlayerControls = styled(motion.div)`
  display: flex; justify-content: center; align-items: center; gap: 15px; width: 100%;
  margin-left: 0; // Default margin (Mobile)

  // Desktop specific margin adjustment
   @media (min-width: 901px) {
       margin-left: 85px; // Apply user's offset only on desktop
   }
`;

const ControlButton = styled(motion.button)`
  background: none; border: none; color: ${({ theme }) => theme.colors.primary}; width: 40px; height: 40px;
  border-radius: 50%; display: flex; justify-content: center; align-items: center; cursor: pointer;
  transition: background-color 0.2s, color 0.2s, transform 0.1s;
  svg { width: 20px; height: 20px; display: block; }
  &:hover:not(:disabled) { background-color: ${({ theme }) => theme.colors.primary + '1A'}; }
  &:active:not(:disabled) { transform: scale(0.95); }
  &:disabled { opacity: 0.5; cursor: not-allowed; background-color: transparent; }
`;
const PlayPauseButton = styled(ControlButton)`
  background-color: ${({ theme }) => theme.colors.primary}; color: white; width: 50px; height: 50px;
  box-shadow: ${({ theme }) => theme.shadows.small};
  svg { width: 24px; height: 24px; path { transform: ${({ theme }) => (theme.colors.secondary === '#FFFFFF' ? 'translateX(1px)' : 'none')}; } }
  &:hover:not(:disabled) { filter: brightness(1.1); background-color: ${({ theme }) => theme.colors.primary}; box-shadow: ${({ theme }) => theme.shadows.medium}; }
`;
const SkipButton = styled(ControlButton)``;

const ProgressBarContainer = styled.div` width: 100%; display: flex; align-items: center; gap: 10px; padding: 0 5px; `;
const TimeDisplay = styled.span` font-size: 0.8em; color: ${({ theme }) => theme.colors.textMuted}; min-width: 35px; text-align: center; `;
const ProgressBar = styled.input<{ $progress: number }>`
  flex-grow: 1; height: 6px; cursor: pointer; appearance: none; background: transparent;
  border-radius: ${({ theme }) => theme.borderRadius.pill}; outline: none;
  &:disabled { cursor: not-allowed;
       &::-webkit-slider-thumb { background: ${({ theme }) => theme.colors.textMuted}; }
       &::-moz-range-thumb { background: ${({ theme }) => theme.colors.textMuted}; }
       &::-webkit-slider-runnable-track { background: ${({ theme }) => theme.colors.border}; }
       &::-moz-range-track { background: ${({ theme }) => theme.colors.border}; }
       &::-moz-range-progress { background-color: transparent; } }
  &::-webkit-slider-runnable-track { width: 100%; height: 100%; background: ${({ theme, $progress }) => `linear-gradient(to right, ${theme.colors.primary} ${$progress}%, ${theme.colors.border} ${$progress}%)`}; border-radius: ${({ theme }) => theme.borderRadius.pill}; transition: background 0.1s ease-out; }
  &::-moz-range-track { width: 100%; height: 100%; background: ${({ theme }) => theme.colors.border}; border-radius: ${({ theme }) => theme.borderRadius.pill}; transition: background 0.1s ease-out; }
  &::-moz-range-progress { background-color: ${({ theme }) => theme.colors.primary}; height: 100%; border-radius: ${({ theme }) => theme.borderRadius.pill}; transition: background 0.1s ease-out; }
  &::-webkit-slider-thumb { appearance: none; margin-top: -3px; width: 12px; height: 12px; background: ${({ theme }) => theme.colors.primary}; border-radius: 50%; border: none; box-shadow: 0 0 4px rgba(0, 0, 0, 0.3); transition: background 0.1s ease-out; }
  &::-moz-range-thumb { width: 12px; height: 12px; background: ${({ theme }) => theme.colors.primary}; border-radius: 50%; border: none; box-shadow: 0 0 4px rgba(0, 0, 0, 0.3); transition: background 0.1s ease-out; }
`;

const BottomControls = styled.div` display: flex; justify-content: space-between; align-items: center; width: 100%; margin-top: 15px; padding: 0 5px; `;
const DonateButton = styled.a`
  display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textOnPrimary}; border-radius: ${({ theme }) => theme.borderRadius.pill}; text-decoration: none;
  font-size: 0.8em; font-weight: 600; box-shadow: ${({ theme }) => theme.shadows.small}; transition: all 0.2s ease;
  svg { width: 14px; height: 14px; }
  &:hover { filter: brightness(1.1); box-shadow: ${({ theme }) => theme.shadows.medium}; transform: translateY(-1px); }
  &:active { transform: scale(0.98); }
`;
const Branding = styled.div`
  font-family: ${({ theme }) => theme.fonts.branding}; font-size: 1.1em; color: ${({ theme }) => theme.colors.textMuted};
  font-weight: bold; text-transform: uppercase; transition: color 0.3s ease;
`;

// --- Animation Variants ---
const characterEnterExitVariants = {
  initial: { opacity: 0, y: 20, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: "easeOut" } },
  exit: { opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.3, ease: "easeIn" } }
};
const trackItemVariants = {
  initial: { x: -20, opacity: 0 },
  animate: (index: number) => ({
    x: 0, opacity: 1, transition: { delay: index * 0.04, duration: 0.3, ease: "easeOut" }
  }),
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
  hover: { x: 3, transition: { duration: 0.15 } },
  tap: { scale: 0.98 }
};
const controlsVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { delay: 0.1, duration: 0.3, ease: "easeOut" } }
};

// --- Helper Function ---
const formatTime = (time: number): string => {
  if (isNaN(time) || time === Infinity || time < 0) { return '0:00'; }
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

// --- Main App Component ---

// Define a default empty track to avoid undefined errors
const defaultTrack: Track = { title: "No Tracks Loaded", artist: "", src: "", cover: "" };
// Define a default empty character to avoid undefined errors
const defaultCharacter: Character = { static: "", animated: "" };

// Define the type for the track object within filteredTracks
interface FilteredTrack extends Track {
    originalIndex: number;
}

function App() {
  // State variables
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [activeCharacterIndex, setActiveCharacterIndex] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [songTitleWidth, setSongTitleWidth] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);

  // Refs
  const audioRef = useRef<HTMLAudioElement>(null);
  const songTitleRef = useRef<HTMLHeadingElement>(null);
  const progressBarRef = useRef<HTMLInputElement>(null);

  // --- Derived State (with improved safety checks) ---
  const safeCurrentTrackIndex = useMemo(() => {
      if (!tracksData || tracksData.length === 0) return 0;
      const index = currentTrackIndex % tracksData.length;
      return index < 0 ? index + tracksData.length : index;
  }, [currentTrackIndex]);

  const currentTrack: Track = useMemo(() => {
      if (!tracksData || tracksData.length === 0) return defaultTrack;
      const safeIndex = (currentTrackIndex % tracksData.length + tracksData.length) % tracksData.length;
      return tracksData[safeIndex] ?? defaultTrack;
  }, [currentTrackIndex]);

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  const safeCharacterIndex = useMemo(() => {
      if (!characterData || characterData.length === 0) return 0;
      const index = activeCharacterIndex % characterData.length;
      return index < 0 ? index + characterData.length : index;
   }, [activeCharacterIndex]);

   const currentCharacter: Character = useMemo(() => {
       if (!characterData || characterData.length === 0) return defaultCharacter;
       const safeIndex = (activeCharacterIndex % characterData.length + characterData.length) % characterData.length;
       return characterData[safeIndex] ?? defaultCharacter;
   }, [activeCharacterIndex]);

  const currentCharacterSrc: string = useMemo(() => {
      return isPlaying ? currentCharacter.animated : currentCharacter.static;
  }, [currentCharacter, isPlaying]);

  const filteredTracks: FilteredTrack[] = useMemo(() => {
    if (!tracksData || tracksData.length === 0) return [];
    const lowerSearchTerm = searchTerm.toLowerCase();
    const mappedTracks = tracksData.map((track, index) => ({ ...track, originalIndex: index }));
    if (!lowerSearchTerm) {
      return mappedTracks;
    }
    return mappedTracks.filter(track =>
        track.title.toLowerCase().includes(lowerSearchTerm) ||
        (track.artist && track.artist.toLowerCase().includes(lowerSearchTerm))
      );
  }, [searchTerm]);


  // --- Playback Control Functions ---
  const playTrack = useCallback((originalIndex: number) => {
    if (!tracksData || tracksData.length === 0) return;
    const actualIndexInData = tracksData.findIndex((t, idx) => idx === originalIndex);
     if (actualIndexInData === -1) { console.error("Could not find track with originalIndex:", originalIndex); return; }
    if (actualIndexInData === safeCurrentTrackIndex && isPlaying) {
      audioRef.current?.pause(); setIsPlaying(false);
    } else {
      setCurrentTrackIndex(actualIndexInData); setIsPlaying(true);
      setCurrentTime(0); setDuration(0);
      if (progressBarRef.current) progressBarRef.current.value = '0';
    }
  }, [safeCurrentTrackIndex, isPlaying]);

  const togglePlayPause = useCallback(() => {
     if (!tracksData || tracksData.length === 0) return;
    setIsPlaying(prevIsPlaying => !prevIsPlaying);
  }, []);

  const nextTrack = useCallback(() => {
    if (!tracksData || tracksData.length === 0) return;
    const newIndex = (safeCurrentTrackIndex + 1) % tracksData.length;
    setCurrentTrackIndex(newIndex); setIsPlaying(true);
    setCurrentTime(0); setDuration(0);
    if (progressBarRef.current) progressBarRef.current.value = '0';
  }, [safeCurrentTrackIndex]);

  const prevTrack = useCallback(() => {
    if (!tracksData || tracksData.length === 0) return;
    const newIndex = (safeCurrentTrackIndex - 1 + tracksData.length) % tracksData.length;
    setCurrentTrackIndex(newIndex); setIsPlaying(true);
    setCurrentTime(0); setDuration(0);
    if (progressBarRef.current) progressBarRef.current.value = '0';
  }, [safeCurrentTrackIndex]);


  // --- Volume and Theme ---
  const handleVolumeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(event.target.value); setVolume(newVolume);
    if (audioRef.current) audioRef.current.volume = newVolume;
  }, []);
  const toggleTheme = useCallback(() => { setIsDarkMode(prevIsDarkMode => !prevIsDarkMode); }, []);

  // --- Progress Bar Handling ---
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current && !isSeeking) {
      const newTime = audioRef.current.currentTime; const currentDuration = audioRef.current.duration;
      if (isFinite(currentDuration) && newTime >= 0 && newTime <= currentDuration) {
          setCurrentTime(newTime);
          if (progressBarRef.current) progressBarRef.current.value = String(newTime);
      }
    }
  }, [isSeeking]);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      const newDuration = audioRef.current.duration;
      if (isFinite(newDuration) && newDuration > 0) {
          setDuration(newDuration);
          if (progressBarRef.current) progressBarRef.current.max = String(newDuration);
      } else {
          console.warn("Invalid duration on loadedmetadata:", newDuration); setDuration(0);
          if (progressBarRef.current) { progressBarRef.current.max = '1'; progressBarRef.current.value = '0'; }
           setCurrentTime(0);
      }
    }
  }, []);

  const handleSeekMouseDown = useCallback(() => { setIsSeeking(true); }, []);
  const handleSeekMouseUp = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
      if (audioRef.current && isFinite(audioRef.current.duration)) {
          const newTime = parseFloat(event.currentTarget.value); audioRef.current.currentTime = newTime;
          setCurrentTime(newTime);
      }
      setIsSeeking(false);
  }, []);
  const handleSeekChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
       if(isSeeking) { const newTime = parseFloat(event.target.value); setCurrentTime(newTime); }
   }, [isSeeking]);

  // --- Effects ---
 useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement || !currentTrack || currentTrack === defaultTrack || !currentTrack.src) {
        if(audioElement && !audioElement.paused) audioElement.pause();
        return;
    }
    let playPromise: Promise<void> | undefined; let isCancelled = false;
    const currentSrc = audioElement.currentSrc || audioElement.src; const newSrc = currentTrack.src;
    const currentPath = currentSrc ? new URL(currentSrc, window.location.origin).pathname : null;
    const newPath = newSrc ? new URL(newSrc, window.location.origin).pathname : null;
    const playWhenReady = () => {
        if (!isCancelled && audioElement.readyState >= 3) {
            playPromise = audioElement.play();
            if (playPromise !== undefined) { playPromise.catch(e => { if (!isCancelled) { console.error("Autoplay failed:", e); setIsPlaying(false); } }); }
        }
        audioElement.removeEventListener('canplaythrough', playWhenReady); audioElement.removeEventListener('error', handleAudioError);
    };
    const handleAudioError = (e: Event) => {
        if (!isCancelled) { console.error("Audio loading error:", e); setIsPlaying(false); }
        audioElement.removeEventListener('canplaythrough', playWhenReady); audioElement.removeEventListener('error', handleAudioError);
    };
    if (!currentPath || currentPath !== newPath) {
        console.log(`Setting audio source to: ${newSrc}`); audioElement.src = newSrc;
        setCurrentTime(0); setDuration(0); if(progressBarRef.current) progressBarRef.current.value = '0';
        audioElement.load();
        if (isPlaying) { audioElement.addEventListener('canplaythrough', playWhenReady); audioElement.addEventListener('error', handleAudioError); }
    } else {
        if (isPlaying && audioElement.paused) {
             if (audioElement.readyState >= 3) {
                playPromise = audioElement.play();
                if (playPromise !== undefined) { playPromise.catch(e => { if (!isCancelled) { console.error("Play failed:", e); setIsPlaying(false); } }); }
             } else { audioElement.addEventListener('canplaythrough', playWhenReady); audioElement.addEventListener('error', handleAudioError); }
        } else if (!isPlaying && !audioElement.paused) { audioElement.pause(); }
    }
    return () => { isCancelled = true; audioElement.removeEventListener('canplaythrough', playWhenReady); audioElement.removeEventListener('error', handleAudioError); };
 }, [currentTrack, isPlaying]);

  useEffect(() => {
    const audioElement = audioRef.current; const handleEnded = () => { setTimeout(() => { console.log("Track ended, playing next"); nextTrack(); }, 100); };
    if (audioElement) audioElement.addEventListener('ended', handleEnded);
    return () => { if (audioElement) audioElement.removeEventListener('ended', handleEnded); };
  }, [nextTrack]);

  useEffect(() => {
      const measureWidth = () => { if (songTitleRef.current) setSongTitleWidth(songTitleRef.current.scrollWidth); }
      const timeoutId = setTimeout(measureWidth, 50); window.addEventListener('resize', measureWidth);
      return () => { clearTimeout(timeoutId); window.removeEventListener('resize', measureWidth); }
  }, [currentTrack.title]);

   useEffect(() => {
    const audioElement = audioRef.current;
    if (audioElement) {
        audioElement.addEventListener('timeupdate', handleTimeUpdate); audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        if (audioElement.readyState >= 1) handleLoadedMetadata(); if (audioElement.readyState >= 2) handleTimeUpdate();
    }
    return () => { if (audioElement) { audioElement.removeEventListener('timeupdate', handleTimeUpdate); audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata); } };
   }, [handleTimeUpdate, handleLoadedMetadata]);

  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyle />
      {/* SVG Definitions */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
         <defs>
          <clipPath id="leftPanelClip" clipPathUnits="objectBoundingBox">
            <path transform="scale(0.002016, 0.001342)" d="M496 484.984C496 504.314 480.33 519.984 461 519.984H444C424.67 519.984 409 535.654 409 554.984V710C409 729.33 393.33 745 374 745H35C15.67 745 0 729.33 0 710V111C0 91.67 15.67 76 35 76H85C104.33 76 120 60.33 120 41V35C120 15.67 135.67 0 155 0H461C480.33 0 496 15.67 496 35V484.984Z" />
          </clipPath>
          <clipPath id="rightPanelClip" clipPathUnits="objectBoundingBox">
            <path transform="scale(0.002703, 0.001342)" d="M370 710C370 729.33 354.33 745 335 745H35C15.67 745 0 729.33 0 710V575.971C0 556.641 15.67 540.971 35 540.971H55.9043C75.2343 540.971 90.9043 525.301 90.9043 505.971V35C90.9043 15.67 106.574 0 125.904 0H335C354.33 0 370 15.67 370 35V710Z" />
          </clipPath>
        </defs>
      </svg>

      <AppContainer>
        <PlayerContainer>
          {/* Left Panel */}
          <LeftPanel>
             <LogoPlaceholder> <img src="/favicon.svg" alt="Logo" onError={(e) => (e.currentTarget.src = 'https://placehold.co/40x40/ef4444/ffffff?text=Y')} /> </LogoPlaceholder>
             <CharacterDisplay>
                 <VolumeContainer> <VolumeLevel $level={volume * 100} /> <VolumeInput type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} aria-label="Volume"/> </VolumeContainer>
                 <AnimatePresence mode="wait">
                     {currentCharacterSrc && (
                         <CharacterImage key={safeCharacterIndex} src={currentCharacterSrc} alt={`Character ${safeCharacterIndex + 1}`} variants={characterEnterExitVariants} initial="initial" animate="animate" exit="exit" />
                     )}
                 </AnimatePresence>
             </CharacterDisplay>
             <div>
                 <SongTitleContainer>
                     <SongTitleTextWrapper $isPlaying={isPlaying} $textWidth={songTitleWidth}>
                         <SongTitle ref={songTitleRef} title={currentTrack?.title ?? ''}> {currentTrack?.title ?? 'Loading...'} </SongTitle>
                     </SongTitleTextWrapper>
                 </SongTitleContainer>
                 <CharacterSelector>
                     {characterData.map((_, index) => (
                         <CharacterDot key={index} $isActive={index === safeCharacterIndex} onClick={() => setActiveCharacterIndex(index)} aria-label={`Select Character ${index + 1}`}> <User size={16} /> </CharacterDot>
                     ))}
                 </CharacterSelector>
             </div>
          </LeftPanel>

          {/* Right Panel */}
          <RightPanel>
             <SearchContainer> <SearchInput type="text" placeholder="SEARCH" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /> </SearchContainer>
             <TracklistContainer>
                 <AnimatePresence>
                     {filteredTracks.map((track, index) => (
                         <TracklistItem
                             key={track.originalIndex} // Use originalIndex as the key, it's guaranteed unique for the original list
                             $isActive={track.originalIndex === safeCurrentTrackIndex}
                             onClick={() => playTrack(track.originalIndex)}
                             variants={trackItemVariants}
                             initial="initial"
                             animate="animate"
                             exit="exit"
                             whileHover="hover"
                             whileTap="tap"
                             custom={index}
                         >
                             {track.title}
                         </TracklistItem>
                     ))}
                 </AnimatePresence>
             </TracklistContainer>
             <PlayerControlsSection>
                 <CoverArtAndThemeToggle>
                     <ThemeToggleSlider onClick={toggleTheme} aria-label="Toggle Theme" />
                     <CoverArtContainer key={safeCurrentTrackIndex} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
                         {currentTrack?.cover && ( <CoverArtImage src={currentTrack.cover} alt={`${currentTrack?.title ?? ''} Cover Art`} /> )}
                     </CoverArtContainer>
                 </CoverArtAndThemeToggle>
                 <PlayerControls variants={controlsVariants} initial="initial" animate="animate">
                     <SkipButton onClick={prevTrack} aria-label="Previous Track" disabled={!tracksData || tracksData.length === 0}> <SkipBack /> </SkipButton>
                     <PlayPauseButton onClick={togglePlayPause} aria-label={isPlaying ? "Pause" : "Play"} disabled={!tracksData || tracksData.length === 0}> {isPlaying ? <Pause fill="white" /> : <Play fill="white" />} </PlayPauseButton>
                     <SkipButton onClick={nextTrack} aria-label="Next Track" disabled={!tracksData || tracksData.length === 0}> <SkipForward /> </SkipButton>
                 </PlayerControls>
                 <ProgressBarContainer>
                     <TimeDisplay>{formatTime(currentTime)}</TimeDisplay>
                     <ProgressBar ref={progressBarRef} type="range" min="0" max={duration > 0 ? String(duration) : '1'} value={isSeeking ? progressBarRef.current?.value : currentTime} onMouseDown={handleSeekMouseDown} onMouseUp={handleSeekMouseUp} onChange={handleSeekChange} aria-label="Track Progress" $progress={duration > 0 ? (currentTime / duration) * 100 : 0} disabled={duration <= 0 || !tracksData || tracksData.length === 0} />
                     <TimeDisplay>{formatTime(duration)}</TimeDisplay>
                 </ProgressBarContainer>
                 <BottomControls>
                     <DonateButton href="https://paypal.me/kuropay" target="_blank" rel="noopener noreferrer"> <DollarSign size={14}/> Donate </DonateButton>
                     <Branding>YohuroBox</Branding>
                 </BottomControls>
             </PlayerControlsSection>
          </RightPanel>
        </PlayerContainer>
      </AppContainer>

      {/* Audio Element */}
      <audio ref={audioRef} preload="metadata"></audio>
    </ThemeProvider>
  );
}

export default App;
